import React from 'react';

/**
 * GuestReminderBand — small inline alert shown above a designated
 * reminder list's body when the current user is signed in as a guest
 * (anonymous). Anonymous accounts can't receive email or push
 * notifications, so any reminder set under one is silently inert.
 * Without this band, the user has no way to know that — they'd set
 * reminders, then wonder why they never get pinged.
 *
 * Walter 2026-05-28 — pairs with the dim-text styling on the list body
 * (see ListPanel / FocusedPanel) and the site-wide GuestUpgradeHint
 * banner. Three layers of signal:
 *
 *   1. ambient — dim body text says "these aren't live."
 *   2. specific — THIS band, on the surface where the user just set
 *      the reminder, says exactly what's wrong and how to fix.
 *   3. proactive — the top GuestUpgradeHint banner fires periodically
 *      after 3 lists / 1 reminder / 2 days, even on lists the user
 *      hasn't visited.
 *
 * The "sign in" affordance routes through the same upgrade callback
 * the AccountPopover uses (parent prop), so the user lands in a
 * familiar flow.
 */
export default function GuestReminderBand({ onSignIn }) {
  return (
    <div
      style={{
        margin: '0 0 8px',
        padding: '7px 10px',
        background: 'rgba(232, 98, 44, 0.10)',
        border: '1px dashed var(--accent-dim, rgba(232,98,44,0.5))',
        borderRadius: 'var(--radius)',
        fontFamily: 'var(--mono)',
        fontSize: 'calc(10px * var(--ui-scale, 1))',
        color: 'var(--accent)',
        lineHeight: 1.5,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
      }}
    >
      <span aria-hidden="true">🔕</span>
      <span style={{ flex: 1 }}>
        reminders need an account to fire.{' '}
        {onSignIn ? (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onSignIn(); }}
            style={{
              background: 'transparent',
              border: 'none',
              padding: 0,
              color: 'var(--accent)',
              fontFamily: 'inherit',
              fontSize: 'inherit',
              cursor: 'pointer',
              textDecoration: 'underline',
              fontWeight: 600,
            }}
          >
            sign in
          </button>
        ) : (
          <strong style={{ fontWeight: 600 }}>sign in</strong>
        )}{' '}
        to enable.
      </span>
    </div>
  );
}
